import {useCallback, useMemo} from "react";
import {Loader} from "@mantine/core";
import {t} from "i18next";
import {
    DIBDocumentGenerator,
    DIBDocumentGeneratorForm,
    DIBDocumentGeneratorView,
} from "dokumentgenerator";
import {dibApi} from "../../../chat/dibApi";
import RenderNodeFactoryLegacy from "../../RenderNodeFactoryLegacy";
import {ruleset} from "../../renderRules";
import {augmentations} from "../../augmentations";
import documentClasses from "./document.module.css";
import {DocumentGeneratorInput} from "./DocumentGeneratorInput";
import {ConditionalsForm} from "./ConditionalsForm";
import {FormLabel} from "./FormLabel";
import {DropdownMenu} from "./DropdownMenu";
import {DropdownMenuItem} from "./DropdownMenuItem";
import {OptionsForm} from "./OptionsForm";
import {XAlternativesForm} from "./XAlternativesForm";
import {XListForm} from "./XListForm";
import {OptionalForm} from "./OptionalForm";
import {UndoButton} from "./UndoButton";

export const DOCUMENT_GENERATOR_PLACEHOLDER_ID = "i";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentViewProps {
    /** topicId of the document template to render */
    documentId: string;
    /** collectionId (filling). Undefined = draft mode */
    collectionId?: string;
    /** Whether user has no saved collection */
    isDraft: boolean;
    /** Callback to open "create filling" modal */
    onPromptNewFilling: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DocumentView = ({
    documentId,
    collectionId,
    isDraft,
    onPromptNewFilling,
}: DocumentViewProps) => {
    // ── Data fetching ────────────────────────────────────────────────

    const isPlaceholder = documentId === DOCUMENT_GENERATOR_PLACEHOLDER_ID;

    const {data: docData, isFetching} =
        dibApi.endpoints.getDocgenDocument.useQuery(
            {resourceId: documentId, collectionId},
            {skip: isPlaceholder, refetchOnMountOrArgChange: true}
        );

    const {data: conditionsData, isFetching: isConditionsFetching} =
        dibApi.endpoints.getResourceConditions.useQuery(
            {resourceId: docData?.topicData?.id ?? ""},
            {skip: !docData?.topicData?.id}
        );

    const {data: conditionUserData} =
        dibApi.endpoints.getResourceConditionUserData.useQuery(
            {topicId: documentId, collectionId},
            {skip: isPlaceholder}
        );

    // ── Mutations ────────────────────────────────────────────────────

    const [triggerPatchXVar] =
        dibApi.endpoints.patchXVariable.useMutation();
    const [triggerSetCondition] =
        dibApi.endpoints.setResourceCondition.useMutation();
    const [triggerAutofill] =
        dibApi.endpoints.autofillDocumentForOrgNr.useLazyQuery();
    const [triggerResetXVars] =
        dibApi.endpoints.resetXVariables.useMutation();
    const [triggerResetConditions] =
        dibApi.endpoints.resetConditionalUserData.useMutation();

    // ── Derived values ───────────────────────────────────────────────

    const documentIds = useMemo(
        () => ({topicId: documentId, collectionId}),
        [documentId, collectionId]
    );

    const showLoader = isFetching || isConditionsFetching;

    // dokumentgenerator expects a react-intl intl object; shim it with i18next
    const intlShim = useMemo(
        () => ({formatMessage: ({id}: {id: string}) => t(id)}),
        []
    );

    // ── Callbacks ────────────────────────────────────────────────────

    const handleSetConditionLabel = useCallback(
        (payload: {
            conditionLabelGroup: string;
            conditionLabel: string;
        }) => {
            triggerSetCondition({
                documentIds,
                ...payload,
            });
        },
        [triggerSetCondition, documentIds]
    );

    const handlePromptNewFilling = useCallback(
        (e?: React.FocusEvent) => {
            if (!isDraft) return;
            (e?.target as HTMLElement)?.blur?.();
            onPromptNewFilling();
        },
        [isDraft, onPromptNewFilling]
    );

    const handleSelectCompany = useCallback(
        ({orgId, variableId, triggerId}: {orgId: string; variableId: string; triggerId: string}) => {
            triggerAutofill({
                orgnr: orgId,
                variableId,
                triggerName: triggerId,
                documentIds,
            });
        },
        [triggerAutofill, documentIds]
    );

    const handleResetDocument = useCallback(() => {
        triggerResetXVars({topicId: documentId, collectionId: collectionId ?? ""});
        triggerResetConditions({topicId: documentId, collectionId});
    }, [triggerResetXVars, triggerResetConditions, documentId, collectionId]);

    // ── Render ───────────────────────────────────────────────────────

    if (isPlaceholder) {
        return (
            <div className={documentClasses.documentView}>
                <p>{t("Info.ChooseADocumentToTheLeft")}</p>
            </div>
        );
    }

    return (
        <div className={documentClasses.documentView}>
            <header className={documentClasses.documentViewHeader}>
                <h1>{docData?.topicData?.name ?? ""}</h1>
            </header>

            {showLoader && (
                <div style={{gridColumn: "1 / -1", textAlign: "center", padding: "2rem"}}>
                    <Loader size="md" />
                </div>
            )}

            {docData?.topicData && !isFetching && !isConditionsFetching && (
                <DIBDocumentGenerator
                    key={docData.topicData.id}
                    documentData2020={docData.topicData}
                    resourceConditionsData={conditionsData ?? {}}
                    conditionsUserData={conditionUserData ?? []}
                    onSetVariableValue={(args: any) =>
                        triggerPatchXVar({...args, ...documentIds})
                    }
                    onSetConditionLabel={handleSetConditionLabel}
                    onSelectProffCompany={handleSelectCompany}
                    onResetDocument={handleResetDocument}
                    intl={intlShim}
                    renderNodeFactory={RenderNodeFactoryLegacy}
                    ruleset={ruleset}
                    augmentations={augmentations}
                    isLoading={isFetching}
                    orgnrSearchUri="/dibapi/api/orgsearch"
                    options={{
                        focusOnClick: false,
                        preventProffLookup: false,
                        preventScroll: false,
                        preventSearch: false,
                        readOnly: false,
                        isDraft,
                    }}
                >
                    <div className={documentClasses.documentForm}>
                        <DIBDocumentGeneratorForm
                            onRenderInput={(
                                inputProps: any,
                                comment: string,
                                companySearchEnabled: boolean,
                                setOpen: (val: boolean) => void,
                                suggestionList: string[],
                                searching: boolean
                            ) => (
                                <DocumentGeneratorInput
                                    inputProps={inputProps}
                                    comment={comment}
                                    companySearchEnabled={
                                        companySearchEnabled
                                    }
                                    setDropdownOpen={setOpen}
                                    suggestionList={suggestionList}
                                    searching={searching}
                                    isDraft={isDraft}
                                    onPromptNewFilling={
                                        handlePromptNewFilling
                                    }
                                />
                            )}
                            onRenderConditionLabels={(props: any) => (
                                <ConditionalsForm {...props} />
                            )}
                            onRenderLabel={(name: string, props: any) => (
                                <FormLabel value={name} {...props} />
                            )}
                            onRenderMenu={(props: any) => (
                                <DropdownMenu {...props} />
                            )}
                            onRenderMenuItem={(props: any) => (
                                <DropdownMenuItem {...props} />
                            )}
                            onRenderOptions={(props: any) => (
                                <OptionsForm
                                    {...props}
                                    onFocus={handlePromptNewFilling}
                                />
                            )}
                            onRenderXAlternativesForm={(props: any) => (
                                <XAlternativesForm
                                    {...props}
                                    onFocus={handlePromptNewFilling}
                                />
                            )}
                            onRenderXListForm={(props: any) => (
                                <XListForm
                                    {...props}
                                    onFocus={handlePromptNewFilling}
                                />
                            )}
                            onRenderXOptionalForm={(props: any) => {
                                const varId =
                                    docData?.topicData?.objects[props.id]
                                        ?.data?.varId;
                                const heading = varId
                                    ? docData?.topicData?.objects[varId]
                                          ?.data?.heading
                                    : undefined;
                                return (
                                    <OptionalForm
                                        {...props}
                                        heading={heading}
                                        onFocus={handlePromptNewFilling}
                                    />
                                );
                            }}
                            onRenderXVarComment={() => null}
                            onRenderXVarReset={(props: any) => (
                                <UndoButton
                                    onClick={props.onClick}
                                    ref={props.ref}
                                />
                            )}
                        />
                    </div>
                    <div className={documentClasses.documentPreview}>
                        <DIBDocumentGeneratorView />
                    </div>
                </DIBDocumentGenerator>
            )}
        </div>
    );
};
